import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Network,
  Globe,
  AlertTriangle,
  Building2,
  MapPin,
  Shield,
  Download,
  Server,
  Eye,
  EyeOff,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// ASN Module Data Types
interface AsnInfo {
  asn: number;
  name?: string;
  description?: string;
  country_code?: string;
  rir?: string;
  email_contacts?: string[];
  abuse_contacts?: string[];
  website?: string;
  ipv4_prefix_count?: number;
  ipv6_prefix_count?: number;
}

// IP Prefix/CIDR Block - matches backend IpPrefix
interface IpPrefix {
  prefix: string;
  ip_version: number;
  asn: number;
  name?: string;
  country_code?: string;
  num_ips: number;
}

// IP Enrichment Data - matches backend IpEnrichment
interface IpEnrichment {
  ip: string;
  ip_type?: string;
  continent_code?: string;
  continent_name?: string;
  country_code?: string;
  country_name?: string;
  region_code?: string;
  region_name?: string;
  city?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
  isp?: string;
  organization?: string;
  asn?: number;
  connection_type?: string;
  is_vpn?: boolean;
  is_proxy?: boolean;
  is_tor?: boolean;
  is_crawler?: boolean;
  threat_level?: string;
}

// Legacy IP Intelligence Data (for backwards compatibility)
interface IpIntel {
  ip: string;
  asn?: number;
  asn_name?: string;
  country?: string;
  city?: string;
  region?: string;
  is_vpn?: boolean;
  is_proxy?: boolean;
  is_tor?: boolean;
  is_datacenter?: boolean;
  datacenter_name?: string;
  isp?: string;
  threat_level?: "high" | "medium" | "low" | "none";
}

// Subdomain ASN mapping
interface SubdomainAsnInfo {
  subdomain: string;
  ips: string[];
  asns: number[];
  providers: string[];
  is_cdn: boolean;
}

// Infrastructure analysis
interface InfrastructureAnalysis {
  unique_asn_count: number;
  dominant_provider?: string;
  uses_cdn: boolean;
  cdn_providers: string[];
  cloud_providers: string[];
  origin_exposed: boolean;
  exposed_subdomains?: string[];
}

export interface AsnModuleData {
  score?: number;
  target?: string;
  asns?: AsnInfo[];
  // IP Prefixes/CIDR blocks (from explicit ASN queries)
  prefixes?: IpPrefix[];
  // Enriched IP samples (from ipstack)
  enriched_ips?: IpEnrichment[];
  // Resolved IPs (domain target)
  resolved_ips?: string[];
  // Legacy field for backwards compatibility
  ips?: IpIntel[];
  total_ips?: number;
  is_shared_hosting?: boolean;
  hosting_provider?: string;
  // Per-subdomain ASN breakdown
  subdomain_asn_map?: SubdomainAsnInfo[];
  // Infrastructure analysis
  infrastructure_analysis?: InfrastructureAnalysis;
  issues?: Array<string | Issue>;
  duration_ms?: number;
}

// Export IPs as CSV
function exportIpsCsv(ips: IpIntel[]) {
  const headers = ["IP", "ASN", "ASN Name", "Country", "City", "Region", "ISP", "Datacenter", "VPN", "Proxy", "Tor", "Threat Level"];
  const csvContent = [
    headers.join(","),
    ...ips.map((ip) => [
      `"${ip.ip}"`,
      `"${ip.asn || ""}"`,
      `"${ip.asn_name || ""}"`,
      `"${ip.country || ""}"`,
      `"${ip.city || ""}"`,
      `"${ip.region || ""}"`,
      `"${ip.isp || ""}"`,
      `"${ip.datacenter_name || ""}"`,
      `"${ip.is_vpn ? "Yes" : "No"}"`,
      `"${ip.is_proxy ? "Yes" : "No"}"`,
      `"${ip.is_tor ? "Yes" : "No"}"`,
      `"${ip.threat_level || "none"}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `ip_inventory_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Export IP Prefixes as CSV
function exportPrefixesCsv(prefixes: IpPrefix[]) {
  const headers = ["Prefix", "IP Version", "ASN", "Name", "Country", "Num IPs"];
  const csvContent = [
    headers.join(","),
    ...prefixes.map((p) => [
      `"${p.prefix}"`,
      `"IPv${p.ip_version}"`,
      `"AS${p.asn}"`,
      `"${p.name || ""}"`,
      `"${p.country_code || ""}"`,
      `"${p.num_ips}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `ip_prefixes_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Format large numbers with abbreviations
function formatIpCount(count: number): string {
  if (count >= 1_000_000_000) {
    return `${(count / 1_000_000_000).toFixed(1)}B`;
  }
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toLocaleString();
}

// IP Prefix Table Columns
function getPrefixColumns(): ColumnDef<IpPrefix>[] {
  return [
    {
      id: "prefix",
      header: "Prefix",
      accessor: (row) => row.prefix,
      sortable: true,
      filterable: true,
      copyable: true,
    },
    {
      id: "ip_version",
      header: "Version",
      accessor: (row) => `IPv${row.ip_version}`,
      cell: (value) => {
        const strValue = value as string;
        const isV6 = strValue === "IPv6";
        return (
          <Badge
            variant="outline"
            className={cn(
              "text-xs",
              isV6
                ? "bg-purple-500/10 text-purple-400 border-purple-500/30"
                : "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"
            )}
          >
            {strValue}
          </Badge>
        );
      },
      sortable: true,
    },
    {
      id: "asn",
      header: "ASN",
      accessor: (row) => `AS${row.asn}`,
      sortable: true,
    },
    {
      id: "name",
      header: "Name",
      accessor: (row) => row.name || "-",
      filterable: true,
    },
    {
      id: "country",
      header: "Country",
      accessor: (row) => row.country_code || "-",
      filterable: true,
    },
    {
      id: "num_ips",
      header: "IPs",
      accessor: (row) => row.num_ips,
      cell: (value) => (
        <span className="font-mono text-cyan-400">{formatIpCount(value as number)}</span>
      ),
      sortable: true,
    },
  ];
}

export interface AsnModuleViewProps {
  data: AsnModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// ASN Card with IP Prefixes
function AsnCard({
  asn,
  prefixes = [],
  resolvedIps = [],
}: {
  asn: AsnInfo;
  prefixes?: IpPrefix[];
  resolvedIps?: string[];
}) {
  const [showPrefixes, setShowPrefixes] = React.useState(false);

  // Filter prefixes for this ASN
  const asnPrefixes = prefixes.filter(p => p.asn === asn.asn);
  const ipv4Prefixes = asnPrefixes.filter(p => p.ip_version === 4);
  const ipv6Prefixes = asnPrefixes.filter(p => p.ip_version === 6);
  const totalIps = asnPrefixes.reduce((sum, p) => sum + p.num_ips, 0);

  // Use actual prefix count if available, otherwise from ASN info
  const ipv4Count = asnPrefixes.length > 0 ? ipv4Prefixes.length : (asn.ipv4_prefix_count || 0);
  const ipv6Count = asnPrefixes.length > 0 ? ipv6Prefixes.length : (asn.ipv6_prefix_count || 0);
  const prefixTotal = ipv4Count + ipv6Count;

  // Max prefixes to show initially
  const MAX_DISPLAY = 10;
  const displayPrefixes = asnPrefixes.slice(0, MAX_DISPLAY);
  const hasMore = asnPrefixes.length > MAX_DISPLAY;

  return (
    <Card className="bg-card/50 hover:border-cyan-500/30 transition-colors">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <Network className="h-4 w-4 text-cyan-400" />
            </div>
            <div>
              <CardTitle className="text-sm font-mono">AS{asn.asn}</CardTitle>
              {asn.country_code && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {asn.country_code}
                </p>
              )}
            </div>
          </div>
          {asn.rir && (
            <Badge variant="outline" className="text-xs">
              {asn.rir}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2 space-y-3">
        <p className="text-sm font-medium">{asn.name || "Unknown Organization"}</p>
        {asn.description && (
          <p className="text-xs text-muted-foreground truncate">
            {asn.description}
          </p>
        )}

        {/* Prefix counts */}
        {prefixTotal > 0 && (
          <div className="flex flex-wrap gap-2">
            {ipv4Count > 0 && (
              <Badge variant="outline" className="text-xs bg-cyan-500/10 border-cyan-500/30">
                {ipv4Count} IPv4 prefixes
              </Badge>
            )}
            {ipv6Count > 0 && (
              <Badge variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30">
                {ipv6Count} IPv6 prefixes
              </Badge>
            )}
            {totalIps > 0 && (
              <Badge variant="outline" className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                {formatIpCount(totalIps)} IPs
              </Badge>
            )}
          </div>
        )}

        {/* Resolved IPs for this ASN (if any) */}
        {resolvedIps.length > 0 && (
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground mb-1.5">Resolved IPs:</p>
            <div className="flex flex-wrap gap-1">
              {resolvedIps.map((ip) => (
                <code key={ip} className="text-xs px-1.5 py-0.5 rounded bg-muted font-mono">
                  {ip}
                </code>
              ))}
            </div>
          </div>
        )}

        {/* IP Prefixes/Blocks */}
        {asnPrefixes.length > 0 && (
          <div className="pt-2 border-t border-border">
            <button
              onClick={() => setShowPrefixes(!showPrefixes)}
              className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors w-full"
            >
              <span>{showPrefixes ? "▼" : "▶"}</span>
              <span>
                {showPrefixes ? "Hide" : "Show"} IP Blocks ({asnPrefixes.length} prefixes)
              </span>
            </button>

            {showPrefixes && (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {displayPrefixes.map((prefix) => (
                  <div
                    key={prefix.prefix}
                    className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50"
                  >
                    <code className="font-mono text-foreground">{prefix.prefix}</code>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] px-1 py-0",
                          prefix.ip_version === 6
                            ? "bg-purple-500/10 text-purple-400 border-purple-500/30"
                            : "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"
                        )}
                      >
                        v{prefix.ip_version}
                      </Badge>
                      <span className="text-muted-foreground font-mono">
                        {formatIpCount(prefix.num_ips)}
                      </span>
                    </div>
                  </div>
                ))}
                {hasMore && (
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    ... and {asnPrefixes.length - MAX_DISPLAY} more prefixes
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// VPN/Proxy/Tor Warning Alert
function PrivacyServiceWarning({ ips }: { ips: IpIntel[] }) {
  const vpnCount = ips.filter((ip) => ip.is_vpn).length;
  const proxyCount = ips.filter((ip) => ip.is_proxy).length;
  const torCount = ips.filter((ip) => ip.is_tor).length;

  if (vpnCount === 0 && proxyCount === 0 && torCount === 0) return null;

  return (
    <div className="p-4 rounded-lg bg-orange-500/5 border border-orange-500/30">
      <div className="flex items-start gap-3">
        <EyeOff className="h-5 w-5 text-orange-400 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-orange-400">Privacy Services Detected</p>
          <p className="text-sm text-muted-foreground mt-1">
            IPs associated with privacy/anonymization services were found.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {vpnCount > 0 && (
              <Badge variant="outline" className="bg-orange-500/10 border-orange-500/30 text-orange-400">
                <Shield className="h-3 w-3 mr-1" />
                {vpnCount} VPN
              </Badge>
            )}
            {proxyCount > 0 && (
              <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-400">
                <Globe className="h-3 w-3 mr-1" />
                {proxyCount} Proxy
              </Badge>
            )}
            {torCount > 0 && (
              <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-400">
                <Eye className="h-3 w-3 mr-1" />
                {torCount} Tor
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Datacenter Summary
function DatacenterSummary({ ips }: { ips: IpIntel[] }) {
  const datacenterIps = ips.filter((ip) => ip.is_datacenter && ip.datacenter_name);
  if (datacenterIps.length === 0) return null;

  // Group by datacenter name
  const byDatacenter = datacenterIps.reduce((acc, ip) => {
    const name = ip.datacenter_name!;
    if (!acc[name]) acc[name] = 0;
    acc[name]++;
    return acc;
  }, {} as Record<string, number>);

  const sorted = Object.entries(byDatacenter).sort((a, b) => b[1] - a[1]);

  return (
    <div className="p-4 rounded-lg bg-muted/30 border border-border">
      <div className="flex items-center gap-2 mb-3">
        <Server className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Datacenter Infrastructure</span>
        <Badge variant="outline" className="text-xs ml-auto">
          {datacenterIps.length} IPs
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        {sorted.map(([name, count]) => (
          <Badge key={name} variant="outline" className="text-xs">
            {name} ({count})
          </Badge>
        ))}
      </div>
    </div>
  );
}

// IP Table Columns
function getIpColumns(): ColumnDef<IpIntel>[] {
  return [
    {
      id: "ip",
      header: "IP Address",
      accessor: (row) => row.ip,
      sortable: true,
      filterable: true,
      copyable: true,
    },
    {
      id: "asn",
      header: "ASN",
      accessor: (row) => row.asn ? `AS${row.asn}` : "-",
      sortable: true,
    },
    {
      id: "location",
      header: "Location",
      accessor: (row) => [row.city, row.country].filter(Boolean).join(", ") || "-",
      filterable: true,
    },
    {
      id: "isp",
      header: "ISP / Provider",
      accessor: (row) => row.isp || row.datacenter_name || "-",
      filterable: true,
    },
    {
      id: "flags",
      header: "Flags",
      accessor: (row) => row,
      cell: (value) => {
        const ip = value as IpIntel;
        const flags = [];
        if (ip.is_vpn) flags.push({ label: "VPN", color: "bg-orange-500/10 text-orange-400 border-orange-500/30" });
        if (ip.is_proxy) flags.push({ label: "Proxy", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" });
        if (ip.is_tor) flags.push({ label: "Tor", color: "bg-purple-500/10 text-purple-400 border-purple-500/30" });
        if (ip.is_datacenter) flags.push({ label: "DC", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30" });

        if (flags.length === 0) return <span className="text-muted-foreground">-</span>;

        return (
          <div className="flex gap-1">
            {flags.map((flag) => (
              <Badge key={flag.label} variant="outline" className={cn("text-xs px-1.5", flag.color)}>
                {flag.label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      id: "threat",
      header: "Threat",
      accessor: (row) => row.threat_level || "none",
      cell: (value) => {
        const level = value as string;
        const colors: Record<string, string> = {
          high: "bg-red-500/10 text-red-400 border-red-500/30",
          medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
          low: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
          none: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
        };
        return (
          <Badge variant="outline" className={cn("text-xs", colors[level] || colors.none)}>
            {level}
          </Badge>
        );
      },
      sortable: true,
    },
  ];
}

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "info";
      if (issue.toLowerCase().includes("vpn") || issue.toLowerCase().includes("proxy")) {
        severity = "medium";
      }
      return { id: `asn-issue-${index}`, severity, message: issue, module: "asn" };
    }
    return issue;
  });
}

export function AsnModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: AsnModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const asns = data.asns || [];
  const prefixes = data.prefixes || [];
  const ips = data.ips || [];

  // Count IP prefixes by version
  const ipv4Prefixes = prefixes.filter((p) => p.ip_version === 4);
  const ipv6Prefixes = prefixes.filter((p) => p.ip_version === 6);
  const totalPrefixIps = prefixes.reduce((sum, p) => sum + p.num_ips, 0);

  // Count privacy service IPs (from legacy ips field)
  // These are used by PrivacyServiceWarning and DatacenterSummary components
  const _vpnCount = ips.filter((ip) => ip.is_vpn).length;
  const _proxyCount = ips.filter((ip) => ip.is_proxy).length;
  const _torCount = ips.filter((ip) => ip.is_tor).length;
  const datacenterCount = ips.filter((ip) => ip.is_datacenter).length;
  void _vpnCount; void _proxyCount; void _torCount; // suppress unused warnings

  return (
    <ModuleContainer
      moduleName="asn"
      icon={Network}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                ASNs Found
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{asns.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                IP Prefixes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {prefixes.length}
              </p>
              {prefixes.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {ipv4Prefixes.length} IPv4 / {ipv6Prefixes.length} IPv6
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Total IPs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {formatIpCount(data.total_ips || totalPrefixIps || ips.length)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Shared Hosting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-lg font-semibold", data.is_shared_hosting ? "text-amber-400" : "text-emerald-400")}>
                {data.is_shared_hosting ? "Yes" : "No"}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Server className="h-3 w-3" />
                Datacenters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {datacenterCount}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Issues
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", issues.length > 0 ? "text-amber-400" : "text-emerald-400")}>
                {issues.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Export Buttons */}
        {(prefixes.length > 0 || ips.length > 0) && (
          <div className="flex justify-end gap-2">
            {prefixes.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportPrefixesCsv(prefixes)}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                Export Prefixes
              </Button>
            )}
            {ips.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportIpsCsv(ips)}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                Export IP Inventory
              </Button>
            )}
          </div>
        )}

        {/* Hosting Provider */}
        {data.hosting_provider && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Primary Hosting Provider</p>
                <p className="font-semibold">{data.hosting_provider}</p>
              </div>
            </div>
          </div>
        )}

        {/* Shared Hosting Warning */}
        {data.is_shared_hosting && (
          <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <div>
                <p className="font-medium text-amber-400">Shared Hosting Detected</p>
                <p className="text-sm text-muted-foreground">
                  This domain appears to be on shared hosting infrastructure.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* VPN/Proxy/Tor Warning */}
        <PrivacyServiceWarning ips={ips} />

        {/* Datacenter Summary */}
        <DatacenterSummary ips={ips} />

        {/* ASN List */}
        {asns.length > 0 && (
          <ModuleSection
            title="Autonomous Systems"
            icon={Globe}
            badge={<Badge variant="outline" className="text-xs">{asns.length}</Badge>}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              {asns.map((asn, index) => (
                <AsnCard
                  key={index}
                  asn={asn}
                  prefixes={prefixes}
                  resolvedIps={asns.length === 1 ? data.resolved_ips : undefined}
                />
              ))}
            </div>
          </ModuleSection>
        )}

        {/* IP Prefixes/CIDR Blocks Table */}
        {prefixes.length > 0 && (
          <ModuleSection
            title="IP Prefixes (CIDR Blocks)"
            icon={Network}
            badge={
              <div className="flex gap-1">
                <Badge variant="outline" className="text-xs">{prefixes.length}</Badge>
                <Badge variant="outline" className="text-xs bg-cyan-500/10 border-cyan-500/30 text-cyan-400">
                  {formatIpCount(totalPrefixIps)} IPs
                </Badge>
              </div>
            }
          >
            <ModuleDataTable
              data={prefixes}
              columns={getPrefixColumns()}
              getRowId={(row) => row.prefix}
              searchPlaceholder="Search prefixes..."
              defaultPageSize={15}
            />
          </ModuleSection>
        )}

        {/* IP Intelligence Table (legacy) */}
        {ips.length > 0 && (
          <ModuleSection
            title="IP Intelligence"
            icon={MapPin}
            badge={<Badge variant="outline" className="text-xs">{ips.length}</Badge>}
          >
            <ModuleDataTable
              data={ips}
              columns={getIpColumns()}
              getRowId={(row) => row.ip}
              searchPlaceholder="Search IPs..."
              defaultPageSize={10}
            />
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

AsnModuleView.displayName = "AsnModuleView";
